import React, { useEffect, useState } from "react"
import { z } from "zod"

import { Button, Dialog, Form, FormInput, useZodForm } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import { NucleoFillIconPicker } from "@/components/nucleo-fill-icon-picker"
import { generateId } from "@/lib/id"
import { mutateOk, useZero } from "@/lib/zero"

const DEFAULT_FOLDER_ICON = "Folder"

interface SnippetFolderDialogProps {
	isOpen: boolean
	onOpenChange: (
		value: boolean,
		createdFolder?: { id: string; name: string },
	) => void
	folder?: { id: string; name: string; icon?: string | null } | null
}

const SnippetFolderFormSchema = z.object({
	name: z.string().min(1),
})

export const SnippetFolderDialog = React.memo(
	({ isOpen, onOpenChange, folder }: SnippetFolderDialogProps) => {
		const zero = useZero()
		const [icon, setIcon] = useState<string>(
			folder?.icon ?? DEFAULT_FOLDER_ICON,
		)
		const form = useZodForm({
			schema: SnippetFolderFormSchema,
			defaultValues: { name: folder?.name ?? "" },
		})

		useEffect(() => {
			if (isOpen) {
				form.reset({ name: folder?.name ?? "" })
				setIcon(folder?.icon ?? DEFAULT_FOLDER_ICON)
			}
		}, [form, isOpen, folder])

		const actionText = folder ? "Rename folder" : "Create folder"

		return (
			<Dialog
				isOpen={isOpen}
				onOpenChange={onOpenChange}
				modal={false}
				onInteractOutside={(event) => {
					// Focus falling back to body while the opening dropdown menu
					// unmounts must not dismiss the dialog; pointer clicks still do.
					if (event.detail.originalEvent.type === "focusin") {
						event.preventDefault()
					}
				}}
				title={actionText}
				footer={
					<Button form="snippet-folder-form" type="submit">
						{actionText}
					</Button>
				}
			>
				<Form
					id="snippet-folder-form"
					form={form}
					className="space-y-4"
					onSubmit={async (values) => {
						if (folder) {
							await mutateOk(
								zero.mutate(
									mutators.snippetFolders.update({
										id: folder.id,
										name: values.name,
										icon,
									}),
								),
							)
							form.reset()
							onOpenChange(false)
						} else {
							const id = generateId()
							await mutateOk(
								zero.mutate(
									mutators.snippetFolders.create({
										id,
										name: values.name,
										icon,
									}),
								),
							)
							form.reset()
							onOpenChange(false, { id, name: values.name })
						}
					}}
				>
					<div className="flex items-center gap-2">
						<NucleoFillIconPicker
							icon={icon}
							onChange={(newIcon) => setIcon(newIcon ?? DEFAULT_FOLDER_ICON)}
							buttonVariant="outline"
							buttonClassName="mb-px size-8"
						/>
						<FormInput
							name="name"
							placeholder="Folder name"
							className="min-h-8 flex-1"
						/>
					</div>
				</Form>
			</Dialog>
		)
	},
)
